"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AR_LOGO_PATHS } from "./ar-logo-paths";
interface ArLogoParticlesProps {
  maxHeight?: number; // Maximum height in pixels
  aspectRatio?: number; // Width to height ratio (default: 2)
  className?: string;
}
type Particles = {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  size: number;
  color: string;
  scatteredColor: string;
  life: number;
  pathIndex: number;
}[];
export default function ArLogoParticles(t0) {
  const $ = _c(37);
  const {
    maxHeight: t1,
    aspectRatio: t2,
    className: t3
  } = t0;
  const maxHeight = t1 === undefined ? 400 : t1;
  const aspectRatio = t2 === undefined ? 2 : t2;
  const className = t3 === undefined ? "" : t3;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      x: 0,
      y: 0
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const mousePositionRef = useRef(t4);
  const isTouchingRef = useRef(false);
  const [, setIsMobile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: 100,
      height: 50
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [dimensions, setDimensions] = useState(t5);
  const animationFrameRef = useRef(0);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const particlesRef = useRef(t6);
  const supportsP3 = typeof window !== "undefined" ? window.matchMedia("(color-gamut: p3)").matches : false;
  const t7 = isDarkMode ? supportsP3 ? "oklch(13.71% 0.036 258.53)" : "#020817" : supportsP3 ? "oklch(98.4% 0.0039 243.25)" : "#f8fafc";
  const t8 = isDarkMode ? supportsP3 ? "oklch(98.4% 0.0039 243.25)" : "#f8fafc" : supportsP3 ? "oklch(13.71% 0.036 258.53)" : "#020817";
  let t9;
  if ($[3] !== t7 || $[4] !== t8) {
    t9 = {
      background: t7,
      logo: t8,
      scattered: "#00DCFF"
    };
    $[3] = t7;
    $[4] = t8;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const colors = t9;
  let t10;
  if ($[6] !== aspectRatio || $[7] !== maxHeight) {
    t10 = () => {
      if (!containerRef.current) {
        return;
      }
      const containerWidth = containerRef.current.clientWidth || 100;
      let width = Math.max(containerWidth, 10);
      let height = Math.max(width / aspectRatio, 10);
      if (height > maxHeight) {
        height = Math.max(maxHeight, 10);
        width = Math.max(height * aspectRatio, 10);
      }
      if (width > containerWidth) {
        width = Math.max(containerWidth, 10);
        height = Math.max(width / aspectRatio, 10);
      }
      width = Math.floor(width);
      height = Math.floor(height);
      setDimensions({
        width,
        height
      });
      setIsMobile(window.innerWidth < 768);
    };
    $[6] = aspectRatio;
    $[7] = maxHeight;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const updateDimensions = t10;
  let t11;
  if ($[9] !== updateDimensions) {
    t11 = () => {
      updateDimensions();
      window.addEventListener("resize", updateDimensions);
      return () => {
        window.removeEventListener("resize", updateDimensions);
      };
    };
    $[9] = updateDimensions;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== aspectRatio || $[12] !== maxHeight || $[13] !== updateDimensions) {
    t12 = [aspectRatio, maxHeight, updateDimensions];
    $[11] = aspectRatio;
    $[12] = maxHeight;
    $[13] = updateDimensions;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[15] !== colors || $[16] !== dimensions || $[17] !== isDarkMode) {
    t13 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      if (dimensions.width <= 0 || dimensions.height <= 0) {
        console.warn("Invalid canvas dimensions:", dimensions);
        return;
      }
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }
      let textImageData = null;
      const createTextImage = function createTextImage() {
        if (!ctx || !canvas) {
          return 0;
        }
        if (canvas.width <= 0 || canvas.height <= 0) {
          console.warn("Invalid canvas dimensions in createTextImage");
          return 0;
        }
        ctx.save();
        ctx.fillStyle = colors.background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const logoSize = Math.max(Math.min(dimensions.width, dimensions.height) * 0.8, 10);
        const scale = logoSize / 512;
        ctx.translate(Math.max(canvas.width / 2 - logoSize / 2, 0), Math.max(canvas.height / 2 - logoSize / 2, 0));
        ctx.scale(scale, scale);
        AR_LOGO_PATHS.forEach((pathData, _index) => {
          const path = new Path2D(pathData.d);
          ctx.fillStyle = colors.logo;
          ctx.fill(path);
        });
        ctx.restore();
        ;
        try {
          textImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = colors.background;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        } catch (t15) {
          const error = t15;
          console.error("Error creating image data:", error);
          return 0;
        }
        return scale;
      };
      const isLogoPartHelper = function isLogoPartHelper(data, index, isDarkMode_0) {
        if (index >= 0 && index + 2 < data.length) {
          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          if (typeof r === "number" && typeof g === "number" && typeof b === "number") {
            return isDarkMode_0 ? r > 200 && g > 200 && b > 200 : r < 50 && g < 50 && b < 50;
          }
        }
        return false;
      };
      const createParticle = function createParticle(scale_0, data_0) {
        if (!ctx || !canvas) {
          return null;
        }
        for (let attempt = 0; attempt < 100; attempt++) {
          const x = Math.floor(Math.random() * canvas.width);
          const y = Math.floor(Math.random() * canvas.height);
          if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) {
            continue;
          }
          const index_0 = (y * canvas.width + x) * 4;
          if (isLogoPartHelper(data_0, index_0, isDarkMode)) {
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            let pathIndex = x < centerX ? 0 : 1;
            const distFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
            const logoRadius = Math.min(dimensions.width, dimensions.height) * 0.4 * scale_0;
            if (Math.abs(distFromCenter - logoRadius) < 10) {
              pathIndex = 2;
            }
            return {
              x,
              y,
              baseX: x,
              baseY: y,
              size: Math.random() * 1.5 + 0.5,
              color: colors.logo,
              scatteredColor: colors.scattered,
              life: Math.random() * 100 + 50,
              pathIndex
            };
          }
        }
        return null;
      };
      const createInitialParticles = function createInitialParticles(scale_1) {
        if (canvas) {
          const canvasArea = Math.max(canvas.width * canvas.height, 1);
          const particleCount = Math.min(Math.floor(5000 * (canvasArea / 500000)), 10000);
          for (let i = 0; i < particleCount; i++) {
            const particle = createParticle(scale_1, textImageData?.data || new Uint8ClampedArray());
            if (particle) {
              particlesRef.current.push(particle);
            }
          }
        }
      };
      function animate(scale_2) {
        if (!ctx || !canvas) {
          return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = colors.background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const {
          x: mouseX,
          y: mouseY
        } = mousePositionRef.current;
        const maxDistance = Math.min(canvas.width, canvas.height) * 0.3;
        for (let i_0 = 0; i_0 < particlesRef.current.length; i_0++) {
          const p = particlesRef.current[i_0];
          if (!p) {
            continue;
          }
          const dx = mouseX - p.x;
          const dy = mouseY - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < maxDistance && (isTouchingRef.current || !("ontouchstart" in window))) {
            const force = (maxDistance - distance) / maxDistance;
            const angle = Math.atan2(dy, dx);
            const moveX = Math.cos(angle) * force * (canvas.width * 0.06);
            const moveY = Math.sin(angle) * force * (canvas.width * 0.06);
            p.x = p.baseX - moveX;
            p.y = p.baseY - moveY;
            ctx.fillStyle = p.scatteredColor;
          } else {
            p.x = p.x + (p.baseX - p.x) * 0.1;
            p.y = p.y + (p.baseY - p.y) * 0.1;
            ctx.fillStyle = p.color;
          }
          ctx.fillRect(p.x, p.y, p.size, p.size);
          p.life = p.life - 1;
          if (p.life <= 0) {
            const newParticle = createParticle(scale_2, textImageData?.data || new Uint8ClampedArray());
            if (newParticle) {
              particlesRef.current[i_0] = newParticle;
            } else {
              particlesRef.current.splice(i_0, 1);
              i_0--;
            }
          }
        }
        animationFrameRef.current = requestAnimationFrame(() => animate(scale_2));
      }
      ;
      try {
        const scale_3 = createTextImage();
        createInitialParticles(scale_3);
        animate(scale_3);
      } catch (t16) {
        const error_0 = t16;
        console.error("Error initializing animation:", error_0);
      }
      const handleMove = (x_0, y_0) => {
        mousePositionRef.current = {
          x: x_0,
          y: y_0
        };
      };
      const handleMouseMove = e => {
        const rect = canvas.getBoundingClientRect();
        handleMove(e.clientX - rect.left, e.clientY - rect.top);
      };
      const handleTouchMove = e_0 => {
        if (e_0.touches.length > 0 && e_0.touches[0]) {
          e_0.preventDefault();
          const rect_0 = canvas.getBoundingClientRect();
          handleMove(e_0.touches[0].clientX - rect_0.left, e_0.touches[0].clientY - rect_0.top);
        }
      };
      const handleTouchStart = () => {
        isTouchingRef.current = true;
      };
      const handleTouchEnd = () => {
        isTouchingRef.current = false;
        mousePositionRef.current = {
          x: 0,
          y: 0
        };
      };
      const handleMouseLeave = () => {
        if (!("ontouchstart" in window)) {
          mousePositionRef.current = {
            x: 0,
            y: 0
          };
        }
      };
      canvas.addEventListener("mousemove", handleMouseMove);
      canvas.addEventListener("touchmove", handleTouchMove, {
        passive: false
      });
      canvas.addEventListener("mouseleave", handleMouseLeave);
      canvas.addEventListener("touchstart", handleTouchStart);
      canvas.addEventListener("touchend", handleTouchEnd);
      return () => {
        canvas.removeEventListener("mousemove", handleMouseMove);
        canvas.removeEventListener("touchmove", handleTouchMove);
        canvas.removeEventListener("mouseleave", handleMouseLeave);
        canvas.removeEventListener("touchstart", handleTouchStart);
        canvas.removeEventListener("touchend", handleTouchEnd);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    };
    t14 = [dimensions, isDarkMode, colors];
    $[15] = colors;
    $[16] = dimensions;
    $[17] = isDarkMode;
    $[18] = t13;
    $[19] = t14;
  } else {
    t13 = $[18];
    t14 = $[19];
  }
  useEffect(t13, t14);
  let t15;
  if ($[20] !== isDarkMode) {
    t15 = () => {
      setIsDarkMode(!isDarkMode);
    };
    $[20] = isDarkMode;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const toggleDarkMode = t15;
  const t16 = `relative w-full ${className}`;
  const t17 = `${maxHeight}px`;
  let t18;
  if ($[22] !== t17) {
    t18 = {
      height: "auto",
      minHeight: "100px",
      maxHeight: t17
    };
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== dimensions.height || $[25] !== dimensions.width) {
    t19 = dimensions.width > 0 && dimensions.height > 0 && <div className="flex items-center justify-center" style={{
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      margin: "0 auto"
    }}><canvas ref={canvasRef} className="touch-none" width={dimensions.width} height={dimensions.height} aria-label="Interactive particle effect with AR logo" /></div>;
    $[24] = dimensions.height;
    $[25] = dimensions.width;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = `rounded-full p-2 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"} text-xs`;
  const t21 = `Switch to ${isDarkMode ? "light" : "dark"} mode`;
  const t22 = isDarkMode ? "\u2600\uFE0F" : "\uD83C\uDF19";
  let t23;
  if ($[27] !== t20 || $[28] !== t21 || $[29] !== t22 || $[30] !== toggleDarkMode) {
    t23 = <div className="absolute right-2 bottom-2 z-10 hidden"><button onClick={toggleDarkMode} className={t20} aria-label={t21}>{t22}</button></div>;
    $[27] = t20;
    $[28] = t21;
    $[29] = t22;
    $[30] = toggleDarkMode;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t16 || $[33] !== t18 || $[34] !== t19 || $[35] !== t23) {
    t24 = <div ref={containerRef} className={t16} style={t18}>{t19}{t23}</div>;
    $[32] = t16;
    $[33] = t18;
    $[34] = t19;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  return t24;
}